import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { ToastOptions, toast } from 'react-toastify';

import { authLogout } from '../actions/authActions';
import { Alert } from '../components/atoms/alert';
import { history } from '../components/history';
import { queryClient } from '../components/queryClient';
import { APP_ROUTES } from '../constants/routes';
import { HttpCodes } from '../enums/httpCodes';
import { store } from '../store';
import { Debounce } from '../utils/debounce';
import { isInternal as isInternalCheck } from './isInternal';

export const REQUEST_TIMEOUT = 'REQUEST_TIMEOUT';

const debouncer = new Debounce(1500);

const getToastOptions = (): ToastOptions | undefined => {
  if (
    window.location.pathname === APP_ROUTES.matchesReviewGrid ||
    window.location.pathname === APP_ROUTES.internalReview ||
    window.location.pathname === APP_ROUTES.groupsReview
  ) {
    return {
      position: 'top-center',
    };
  }

  return undefined;
};
export abstract class HttpService {
  static getToken = async (): Promise<string | null> => {
    try {
      // If that goes well, get the token:
      const session = await fetchAuthSession();
      const { idToken } = session?.tokens ?? {};
      return idToken?.toString() || null;
    } catch (err) {
      new Alert('You have been logged out!', 'OK').alert();
      debouncer.debounce(() => {
        store.dispatch(authLogout());
      });
      console.trace(err);
      return null;
    }
  };

  static downloadFile = async (url: string, withoutAuth = false): Promise<Response> => {
    if (withoutAuth) {
      return fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'default',
      }).catch((err) => HttpService.handleResponseError(err, true));
    }

    const config = await getConfig();
    return fetch(url, {
      method: 'GET',
      headers: config.headers,
      mode: 'cors',
      cache: 'default',
    }).catch((err) => HttpService.handleResponseError(err, true));
  };

  static fetchAuthorizedFleetless = async (url: string, cfg: RequestInit = {}, abortSignal?: AbortSignal, localErrorHandling?: boolean) => {
    const config = await getConfig(cfg, abortSignal);

    return fetch(url, config)
      .then((res) => HttpService.handleResponse(res, localErrorHandling))
      .catch((err) => {
        return HttpService.handleResponseError(err, localErrorHandling);
      });
  };

  static fetchAuthorized = async (url: string, cfg: RequestInit = {}, abortSignal?: AbortSignal, localErrorHandling?: boolean) => {
    const config = await getConfig(cfg, abortSignal);

    const isInternal = await isInternalCheck();

    if (isInternal) {
      const fleetID = store.getState().store.fleet?.uuid;
      if (fleetID) {
        const currentUrl = new URL(url, new URL(document.URL).origin);
        currentUrl.searchParams.set('fleet_id', fleetID);
        url = currentUrl.toString();
      }
    }

    return fetch(url, config)
      .then((res) => HttpService.handleResponse(res, localErrorHandling))
      .catch((err) => {
        return HttpService.handleResponseError(err, localErrorHandling);
      });
  };

  static fetchAuthorizedFile = async (url: string, cfg: RequestInit = {}, abortSignal?: AbortSignal, localErrorHandling?: boolean) => {
    const config = await getConfig(cfg, abortSignal);

    return fetch(url, config);
  };

  static fetchAuthorizedFileWithFleet = async (
    url: string,
    cfg: RequestInit = {},
    abortSignal?: AbortSignal,
    localErrorHandling?: boolean,
  ) => {
    const config = await getConfig(cfg, abortSignal);

    const isInternal = await isInternalCheck();

    if (isInternal) {
      const fleetID = store.getState().store.fleet?.uuid;
      if (fleetID) {
        const currentUrl = new URL(url, new URL(document.URL).origin);
        currentUrl.searchParams.set('fleet_id', fleetID);
        url = currentUrl.toString();
      }
    }

    return fetch(url, config);
  };

  public static handleSavingError = (error: any, setLoadingCb: Function) => {
    if (typeof error.json === 'function') {
      error.json().then((err: any) => {
        if (err.message && typeof err.message === 'string') {
          const preparedMessage = err.message[0].toUpperCase() + err.message.slice(1);
          new Alert(preparedMessage).alert();
        }

        err?.errors?.forEach((pathErrorPair) => {
          Object.entries(pathErrorPair).forEach(([key, value]) => {
            toast.error(`${key}: ${value}`, getToastOptions());
          });
        });
      });
    } else {
      toast.error(
        error.message || 'Error occured during saving. Please, check your network conditions and try again later',
        getToastOptions(),
      );
    }
    setLoadingCb(false);
  };

  private static handleResponseError = (error: any, localErrorHandling: boolean) => {
    // maybe localerrorhandling could be inside extendable config
    if (error?.name === 'AbortError') {
      return Promise.resolve(null);
    }
    if (error?.message === REQUEST_TIMEOUT) {
      isInternal().then((isInternal) => isInternal && toast.error('An error occured: Request timeout.', getToastOptions()));
      return Promise.resolve(REQUEST_TIMEOUT); // maybe should be promise reject
    }
    if (localErrorHandling) {
      return Promise.reject(error);
    }
    if (error?.message === 'Failed to fetch') {
      error.message += '. Check your internet connection.';
    }
    error.message && isInternal().then((isInternal) => isInternal && toast.error(`An error occured: ${error.message}`, getToastOptions()));
    console.info(`Caught an error! Error ${error}`);
    console.error(error);
    return Promise.reject(null); // maybe should be reject with error object
  };

  private static handleResponse = async (res: any, noErrorHandling: boolean) => {
    if (res.status === HttpCodes.TIMEOUT) {
      throw new Error(REQUEST_TIMEOUT);
    }
    const response: Response = res.clone ? res.clone() : res;
    // FIXME Mocked fetch doesn't have support for response.clone() method.
    // Its crippled. Ultimately this function should be executed in its whole!
    if ((window as any).Cypress) {
      // return res;
    }

    if (
      response.status === HttpCodes.OK ||
      response.status === HttpCodes.CREATED ||
      response.status === HttpCodes.MULTI_STATUS ||
      response.status === HttpCodes.ACCEPTED ||
      response.status === HttpCodes.NO_CONTENT
    ) {
      try {
        const r = await res.json();
        return r;
      } catch (e) {
        try {
          return await response.arrayBuffer();
        } catch (e) {
          return Promise.resolve({});
        }
      }
    } else if (response.status === HttpCodes.FORCE_RELOGIN) {
      // this is a rare case, where a user that previously existed in the system logs in using SSO for the first time.
      // in such case, the user is recreated and merged on cognito end of things and current token may contain wrong groups or fleet etc.
      // therefore we need to re-authenticate the user to get the correct groups and fleet from the new, merged user.
      // Once thats done, we should be to able to re-query everything
      store.dispatch(authLogout());
      await signOut();
      signInWithRedirect({ provider: 'Google' });
    } else {
      if (response.status === HttpCodes.UNAUTHORIZED) {
        // two cases here. If you're impersonating a user, you may have turned the impersonation on
        // inside a screen that the impersonated user does not have access to. In such case, we dont want
        // to do a full logout, but rather redirect to the main page of portal.
        // Second case is when you're yourself, then we want to do a full logout cause something is wrong,
        // as UI should prevent from firing any unauthorized requests.
        const impersonatedUser = await queryClient.getQueryData(['impersonatedUser']);

        if (impersonatedUser) {
          // go to root page without reload by writing to react router history
          history.push('/');
        } else {
          const idx = response.url?.indexOf('/', 10); // skip protocol
          let path = response.url?.substring(idx);
          path = path.split('?')?.[0]; // remove query params

          isInternal().then((isInternal) => {
            let message = 'You have no permission to access this resource';
            if (isInternal) {
              message += `: ${path}`;
            }
            toast.error(message, getToastOptions());
          });
        }
      } else if (response.status === HttpCodes.MAINTENANCE) {
        window.location.href = '/maintenance.html';
      } else if (response.status !== HttpCodes.OK && response.status !== 0 && !noErrorHandling) {
        let message = `An unexpected error occured! Error ${response.status}`;
        const body = await response.json();
        if (body.message) {
          message += `: ${body.message}`;
        }

        isInternal().then((isInternal) => isInternal && toast.error(message, getToastOptions()));
        console.error(message);
      }
      return Promise.reject(response);
    }
  };
}

const isInternal = (): Promise<boolean> => {
  return isInternalCheck();
};

const getConfig = async (cfg?: RequestInit, abortSignal?: AbortSignal) => {
  const token = await HttpService.getToken();
  const impersonatedUser = await queryClient.getQueryData(['impersonatedUser']);

  if (!token) {
    debouncer.debounce(() => {
      store.dispatch(authLogout());
      window.location.href = '/';
    });
    throw new Error('No token');
  }

  const config: RequestInit = { ...cfg };

  config.headers = {
    Authorization: `Bearer ${token}`,
    ...(cfg ?? {}).headers,
  };

  if (abortSignal) {
    config.signal = abortSignal;
  }

  if (impersonatedUser) {
    config.headers['X-Authenticated-As'] = impersonatedUser;
  }

  return config;
};
